/**
 * 앵커가 사라지면 popup을 닫는 계층. 앵커 element가 DOM에서 분리되거나 scroll/resize로 viewport나
 * overflow 조상을 완전히 벗어나면 다음 프레임에 `onClose`. 판정은 `anchorVisibility`가 맡고 이 hook은
 * 언제 검사할지와 재앵커 중의 오탐 방지만 소유한다.
 */
import { useEffect, useRef } from "react";

import { collectClipAncestors, isAnchorElementHidden } from "./anchorVisibility";

export function useAnchorHiddenClose({
  open,
  enabled,
  anchorEl,
  onClose,
}: {
  open: boolean;
  enabled: boolean;
  /** rect 앵커의 element. point/수동 rect 앵커는 null이라 대상이 아니다. */
  anchorEl: HTMLElement | null;
  onClose: () => void;
}) {
  // onClose는 매 렌더 바뀔 수 있고, 최신 anchorEl은 rAF 시점의 identity 가드에 쓴다 — 둘 다 effect를
  // 재실행하지 않도록 ref로 읽는다.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const latestAnchorElRef = useRef(anchorEl);
  latestAnchorElRef.current = anchorEl;

  useEffect(() => {
    if (!open || !enabled || !anchorEl) return;

    // 잘라내는 조상은 열린 동안 바뀌지 않는다고 보고 한 번만 걷는다 — 매 tick getComputedStyle 순회는 깊은 트리에서 jank.
    const clipAncestors = collectClipAncestors(anchorEl);

    // 판정은 rAF로 미룬다 — 소비처가 다른 element로 재앵커하는 동안(예: 행이 다른 페이지로 가면 목록
    // viewport에 붙는 미리보기) mutation microtask가 먼저 닫아 버리지 않게 한다. 재앵커면 이 effect가
    // 먼저 정리돼 여기 오지 않고, 그래도 도달했다면 최신 anchor가 이 el이 아닐 때 옛 el의 분리를 닫힘으로
    // 보지 않는다.
    let raf = 0;
    const check = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (latestAnchorElRef.current !== anchorEl) return;
        if (
          isAnchorElementHidden(anchorEl, clipAncestors, {
            width: window.innerWidth,
            height: window.innerHeight,
          })
        ) {
          onCloseRef.current();
        }
      });
    };

    // 열릴 때 이미 분리된 el(같은 update에서 행이 필터로 사라진 경우)은 다음 프레임의 검사가 닫는다.
    if (!anchorEl.isConnected) check();
    window.addEventListener("resize", check, { passive: true });
    window.addEventListener("scroll", check, { passive: true, capture: true });
    // 행 삭제·목록 갱신처럼 scroll 없이 앵커만 떨어져 나가는 경우는 scroll/resize tick이 오지 않는다.
    // 콜백은 batch마다 isConnected 하나지만, 브라우저는 열린 동안 body subtree의 모든 mutation record를
    // 만든다 — DOM이 자주 바뀌는 화면에서 popup을 오래 열어 두면 그만큼의 비용이 든다. 앵커 조상 체인마다
    // childList 관찰로 좁힐 수 있으나 복잡도 대비 이득이 작아 두지 않는다.
    const detachObserver =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(() => {
            if (!anchorEl.isConnected) check();
          })
        : null;
    detachObserver?.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (raf) cancelAnimationFrame(raf);
      detachObserver?.disconnect();
      window.removeEventListener("resize", check);
      window.removeEventListener("scroll", check, true);
    };
  }, [anchorEl, enabled, open]);
}
